import { useTranslation } from 'react-i18next'
import { Download, FileText, Plus, Send, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { formatDateTime } from '@/lib/format'
import { cn } from '@/lib/utils'
import { CREDIT_NOTE_REASON_LABELS, type CreditNote } from '@/types'
import { InvoiceTabSectionCard } from './InvoiceTabSection'
import { formatInvoiceAmount } from './invoiceTabStyles'

export function InvoiceCreditNotesTab({
  creditNotes,
  isLoading,
  isError,
  canMutateInvoices,
  canCreateCreditNote,
  onCreateCreditNote,
  onSendCreditNote,
  onExportCreditNote,
  onDeleteCreditNote,
}: {
  creditNotes: CreditNote[]
  isLoading: boolean
  isError: boolean
  canMutateInvoices: boolean
  canCreateCreditNote: boolean
  onCreateCreditNote: () => void
  onSendCreditNote: (creditNote: CreditNote) => void
  onExportCreditNote: (creditNote: CreditNote) => void
  onDeleteCreditNote?: (creditNote: CreditNote) => void
}) {
  const { t } = useTranslation('invoices')
  const creditNoteActionClass =
    'flex h-6 shrink-0 items-center justify-center rounded-lg border border-[#e7e9ef] bg-white px-1.5 py-1 text-xs font-medium leading-4 text-[#171a20] shadow-[0_1px_4px_rgba(38,45,66,0.05)] transition-colors'
  const orangeActionClass = cn(
    creditNoteActionClass,
    'hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f]'
  )
  const deleteActionClass = cn(
    creditNoteActionClass,
    'hover:border-[#d63b33] hover:bg-[#fef2f2] hover:text-[#d63b33]'
  )

  return (
    <InvoiceTabSectionCard>
      <div className='flex h-16 items-center gap-2 overflow-hidden px-6 pb-2 pt-6'>
        <div className='flex min-w-0 flex-1 items-center gap-3 overflow-hidden'>
          <span className='flex size-8 shrink-0 items-center justify-center rounded-lg bg-[#fde9e8] text-[#d92d20]'>
            <FileText className='size-4' strokeWidth={2.2} />
          </span>
          <h2 className='truncate text-sm font-semibold leading-[1.2] text-[#171a20]'>
            {t('detail.creditNotesTitle')}
          </h2>
        </div>
      </div>
      <div className='flex w-full flex-col items-center gap-4 px-6 py-4'>
        {isLoading ? (
          <p className='py-3 text-center text-sm leading-[1.4] text-[#5a5a5a]'>
            {t('detail.loading')}
          </p>
        ) : isError ? (
          <p className='py-3 text-center text-sm leading-[1.4] text-destructive'>
            {t('detail.creditNotesLoadError')}
          </p>
        ) : creditNotes.length === 0 ? (
          <p className='py-3 text-center text-sm leading-[1.4] text-[#5a5a5a]'>
            {t('detail.noCreditAtMoment')}
          </p>
        ) : (
          <div className='flex w-full flex-col gap-3'>
            {creditNotes.map(creditNote => {
              const reason = creditNote.reason
                ? (CREDIT_NOTE_REASON_LABELS[creditNote.reason] ??
                  creditNote.reason)
                : '\u2014'

              return (
                <div
                  key={creditNote.id}
                  className='flex w-full min-w-0 flex-col gap-3 rounded-xl border border-[#e7e9ef] bg-[#f4f5f8] p-3 sm:flex-row sm:items-center'
                >
                  <div className='flex min-w-0 flex-1 flex-wrap items-center gap-2 text-sm leading-[1.4]'>
                    <span className='shrink-0 whitespace-nowrap font-medium text-[#171a20]'>
                      {creditNote.number ?? '\u2014'}
                    </span>
                    <span className='size-1 shrink-0 rounded-full bg-[#3d434e]' />
                    <span className='min-w-0 max-w-full truncate text-[#3d434e]'>
                      {reason}
                    </span>
                    <span className='size-1 shrink-0 rounded-full bg-[#3d434e]' />
                    <span className='shrink-0 whitespace-nowrap text-[#3d434e]'>
                      {formatDateTime(creditNote.created_at)}
                    </span>
                  </div>
                  <div className='flex shrink-0 items-center justify-between gap-3 sm:justify-end'>
                    <span className='whitespace-nowrap text-sm font-medium leading-[1.4] text-[#b91c1c]'>
                      - {formatInvoiceAmount(creditNote.total)}
                    </span>
                    <div className='flex shrink-0 items-center gap-2'>
                      {canMutateInvoices && (
                        <TruncatedTextTooltip
                          alwaysShow
                          closeOnPointerDown
                          className='inline-flex shrink-0'
                          content={t('creditNotes.send')}
                          delayDuration={0}
                        >
                          <button
                            type='button'
                            className={cn(orangeActionClass, 'w-6 px-1')}
                            aria-label={t('creditNotes.send')}
                            onClick={() => onSendCreditNote(creditNote)}
                          >
                            <Send className='size-3.5' />
                          </button>
                        </TruncatedTextTooltip>
                      )}
                      <TruncatedTextTooltip
                        alwaysShow
                        closeOnPointerDown
                        className='inline-flex shrink-0'
                        content={t('creditNotes.download')}
                        delayDuration={0}
                      >
                        <button
                          type='button'
                          className={cn(orangeActionClass, 'px-1')}
                          aria-label={t('creditNotes.download')}
                          onClick={() => onExportCreditNote(creditNote)}
                        >
                          <Download className='size-3.5' />
                        </button>
                      </TruncatedTextTooltip>
                      {canMutateInvoices &&
                        creditNote.status === 'draft' &&
                        onDeleteCreditNote && (
                          <TruncatedTextTooltip
                            alwaysShow
                            closeOnPointerDown
                            className='inline-flex shrink-0'
                            content={t('creditNotes.delete')}
                            delayDuration={0}
                          >
                            <button
                              type='button'
                              className={cn(deleteActionClass, 'w-6 px-1')}
                              aria-label={t('creditNotes.delete')}
                              onClick={() => onDeleteCreditNote(creditNote)}
                            >
                              <X className='size-4' />
                            </button>
                          </TruncatedTextTooltip>
                        )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
      {canCreateCreditNote ? (
        <div className='flex w-full flex-col items-end gap-3 px-6 pb-6 pt-2'>
          <Button
            type='button'
            className='h-auto shrink-0 rounded-xl border border-primary bg-primary p-3 text-sm font-medium leading-none text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground'
            onClick={onCreateCreditNote}
          >
            <Plus className='mr-2 size-4' />
            {t('detail.createCreditNote')}
          </Button>
        </div>
      ) : null}
    </InvoiceTabSectionCard>
  )
}
